import { Component, OnInit } from '@angular/core';
import { TradingDataService, WalletBalanceDto } from '../../../../services/tradingdata.service';

@Component({
  selector: 'app-trading-dashboard-summary',
  templateUrl: './trading-dashboard-summary.component.html',
  styleUrls: ['./trading-dashboard-summary.component.css']
})
export class TradingDashboardSummaryComponent implements OnInit {

  balances: WalletBalanceDto[] = [];

  loading = false;
  error: string | null = null;
  token!: string | null;

  // exchange filter
  selectedExchange: string = 'ALL';

  constructor(private tradingDataService: TradingDataService) {}

  ngOnInit(): void {
    const userStr = sessionStorage.getItem('wOSUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.token = user.stsTokenManager?.accessToken || null;
      } catch (e) {
        console.error('Error parsing wOSUser from sessionStorage', e);
        this.token = null;
      }
    } else {
      this.token = null;
    }

    if (this.token) {
      this.loadBalances();
    } else {
      this.error = 'No auth token found for summary dashboard.';
    }
  }

  async loadBalances(): Promise<void> {
    if (!this.token) return;

    this.loading = true;
    this.error = null;

    try {
      const data = await this.tradingDataService.getWalletBalances(this.token);
      this.balances = data || [];

      // if current selection no longer exists, reset to ALL
      if (this.selectedExchange !== 'ALL' && !this.exchanges.includes(this.selectedExchange)) {
        this.selectedExchange = 'ALL';
      }
    } catch (err: any) {
      console.error('Error loading wallet balances:', err);
      this.error = err?.message || 'Failed to load wallet balances.';
    } finally {
      this.loading = false;
    }
  }

  // --- derived / helper properties ---

  hasData(): boolean {
    return this.balances && this.balances.length > 0;
  }

  // list of distinct exchanges from the data
  get exchanges(): string[] {
    return Array.from(new Set(this.balances.map(b => b.Exchange))).sort();
  }

  // balances respecting exchange filter
  get filteredBalances(): WalletBalanceDto[] {
    if (this.selectedExchange === 'ALL') {
      return this.balances;
    }
    return this.balances.filter(b => b.Exchange === this.selectedExchange);
  }

  // totals for the filtered view
  get totalBalance(): number {
    return this.filteredBalances.reduce((sum, b) => sum + Number(b.Balance || 0), 0);
  }

  get totalAvailable(): number {
    return this.filteredBalances.reduce((sum, b) => sum + Number(b.Available || 0), 0);
  }

  get totalHold(): number {
    return this.filteredBalances.reduce((sum, b) => sum + Number(b.Hold || 0), 0);
  }

  get totalPositions(): number {
    return this.filteredBalances.length;
  }

  get distinctCurrencies(): number {
    return new Set(this.filteredBalances.map(b => b.Currency)).size;
  }

  get distinctExchanges(): number {
    return new Set(this.filteredBalances.map(b => b.Exchange)).size;
  }
}
